import { BrowserUtil } from 'kuaidong-lib'

const keyboardScrollDelay = 300
const keyboardResetDelay = 100
const popupMainSelector = '.kd-popup__main'

export type KeyboardAdjustContext = {
  adjustForKeyboard: boolean
  disabled?: boolean
  focusTimer: ReturnType<typeof setTimeout> | null
  $el?: Element | null
}

type PopupKeyboardAdjustState = {
  popupEl: HTMLElement
  onViewportChange: () => void
  resetTimer: ReturnType<typeof setTimeout> | null
}

let activePopupAdjust: PopupKeyboardAdjustState | null = null

export function clearKeyboardFocusTimer(_ctx: KeyboardAdjustContext) {
  if (_ctx.focusTimer) {
    clearTimeout(_ctx.focusTimer)
    _ctx.focusTimer = null
  }
}

function findPopupMain(_el: HTMLElement) {
  return _el.closest(popupMainSelector) as HTMLElement | null
}

function findFocusableInput(_el: HTMLElement) {
  if (_el.matches('input, textarea')) {
    return _el
  }

  return _el.querySelector('input, textarea') as HTMLElement | null
}

function getKeyboardInset() {
  const vv = window.visualViewport

  if (!vv) {
    return 0
  }

  const inset = window.innerHeight - vv.height - vv.offsetTop

  return Math.max(0, Math.round(inset))
}

function schedulePopupOffsetUpdate() {
  if (!activePopupAdjust) {
    return
  }

  requestAnimationFrame(() => {
    if (!activePopupAdjust) {
      return
    }

    const inset = getKeyboardInset()
    activePopupAdjust.popupEl.style.bottom = inset > 0 ? `${inset}px` : ''
  })
}

function teardownPopupAdjust() {
  if (!activePopupAdjust) {
    return
  }

  const { popupEl, onViewportChange, resetTimer } = activePopupAdjust

  if (resetTimer) {
    clearTimeout(resetTimer)
  }

  window.visualViewport?.removeEventListener('resize', onViewportChange)
  window.visualViewport?.removeEventListener('scroll', onViewportChange)
  popupEl.style.bottom = ''
  activePopupAdjust = null
}

function attachPopupKeyboardAdjust(_popupEl: HTMLElement) {
  if (activePopupAdjust?.popupEl === _popupEl) {
    schedulePopupOffsetUpdate()
    return
  }

  teardownPopupAdjust()

  const onViewportChange = () => schedulePopupOffsetUpdate()

  activePopupAdjust = {
    popupEl: _popupEl,
    onViewportChange,
    resetTimer: null,
  }

  window.visualViewport?.addEventListener('resize', onViewportChange)
  window.visualViewport?.addEventListener('scroll', onViewportChange)
  schedulePopupOffsetUpdate()
}

function adjustElementForKeyboard(_el: HTMLElement) {
  const popupEl = findPopupMain(_el)

  if (popupEl) {
    attachPopupKeyboardAdjust(popupEl)
  }

  const focusEl = findFocusableInput(_el)

  if (focusEl) {
    focusEl.scrollIntoView({ block: 'center', inline: 'nearest' })
  } else {
    _el.scrollIntoView({ block: 'center', inline: 'nearest' })
  }
}

export function scrollIntoViewForKeyboard(_ctx: KeyboardAdjustContext) {
  if (!_ctx.adjustForKeyboard || _ctx.disabled || !BrowserUtil.isIOS()) {
    return
  }

  const el = _ctx.$el as HTMLElement | null | undefined

  if (!el) {
    return
  }

  clearKeyboardFocusTimer(_ctx)

  _ctx.focusTimer = setTimeout(() => {
    adjustElementForKeyboard(el)
    _ctx.focusTimer = null
  }, keyboardScrollDelay)
}

export function dismissKeyboardAdjust(_ctx: KeyboardAdjustContext) {
  if (!_ctx.adjustForKeyboard || !BrowserUtil.isIOS()) {
    return
  }

  const el = _ctx.$el as HTMLElement | null | undefined
  const popupEl = el ? findPopupMain(el) : null

  if (!popupEl || activePopupAdjust?.popupEl !== popupEl) {
    return
  }

  if (activePopupAdjust.resetTimer) {
    clearTimeout(activePopupAdjust.resetTimer)
  }

  activePopupAdjust.resetTimer = setTimeout(() => {
    if (!activePopupAdjust || activePopupAdjust.popupEl !== popupEl) {
      return
    }

    activePopupAdjust.resetTimer = null

    if (popupEl.contains(document.activeElement)) {
      return
    }

    teardownPopupAdjust()
  }, keyboardResetDelay)
}

export function releaseKeyboardAdjust(_ctx: KeyboardAdjustContext) {
  clearKeyboardFocusTimer(_ctx)

  const el = _ctx.$el as HTMLElement | null | undefined

  if (el && activePopupAdjust?.popupEl.contains(el)) {
    teardownPopupAdjust()
  }
}
